import { useState, type FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { describeError } from '../../ui/problemDetails.ts';
import { createList, listKeys } from './itemsApi.ts';

/**
 * Adds another list to the group — "DIY", "Christmas", "Big shop".
 *
 * One field and one button: making a list is a once-in-a-while act and does not need
 * explaining every time you open settings.
 */
export function NewListForm({ groupId }: { groupId: string }) {
  const queryClient = useQueryClient();
  const [name, setName] = useState('');

  const create = useMutation({
    mutationFn: (listName: string) => createList(groupId, listName),
    onSuccess: async () => {
      setName('');
      await queryClient.invalidateQueries({ queryKey: listKeys.ofGroup(groupId) });
    },
  });

  const trimmed = name.trim();

  function handleSubmit(event: FormEvent) {
    event.preventDefault();

    if (trimmed.length > 0) {
      create.mutate(trimmed);
    }
  }

  return (
    <section className="settings-section">
      <h3 className="settings-section__title">New list</h3>

      <form className="settings-row" onSubmit={handleSubmit}>
        <input
          className="settings-row__input"
          value={name}
          onChange={(event) => setName(event.target.value)}
          placeholder="DIY"
          aria-label="New list name"
          maxLength={80}
        />
        <button
          className="button button--primary button--small"
          type="submit"
          disabled={create.isPending || trimmed.length === 0}
        >
          Add
        </button>
      </form>

      {create.isError && (
        <p className="form-error" role="alert">
          {describeError(create.error)}
        </p>
      )}
    </section>
  );
}
